import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@nextui-org/react";
import React, { useEffect, useState } from "react";
import MarkdownEditor from "../Dashboard/MarkdownEditor";
import { UpdateOneData, getDataWithFilterUrl } from "../../controllers/APIController";
import { StandardCancelButton, StandardDeleteButton } from "../../components/StandardButtons";

/**
 * EditPolicyFrameWorkModal Component
 * Modal for editing policy framework content using a markdown editor
 * Allows users to update policy framework text and saves changes to backend
 * @param {Object} props - Component props
 * @param {Function} props.onSubmitHandler - Function to handle form submission (currently unused)
 * @param {number} props.id - Policy ID to edit
 * @param {string} props.modalHeading - Modal heading text (currently unused)
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onOpen - Function to open the modal
 * @param {Function} props.onOpenChange - Function to handle modal open/close state changes
 * @param {Function} props.onReRender - Function to trigger page re-render after update
 * @returns {JSX.Element} Policy framework edit modal
 */
const EditPolicyFrameWorkModal = ({
  onSubmitHandler,
  id,
  modalHeading,
  isOpen,
  onOpen,
  onOpenChange,
  onReRender
}) => {
  // State for markdown content
  const [markdownContent, setMarkdownContent] = useState("");
  
  // User and organization information from session storage
  const userLogged = JSON.parse(sessionStorage.getItem("userData"));
  const orgInfo = userLogged && userLogged.user.LastOrgId;
  const orgFilterUrl = "filters[Organization][id][$eqi]=" + orgInfo;

  /**
   * Handle form submission to update policy framework
   * Updates the policy with new markdown content and closes modal
   * @param {Function} onClose - Function to close the modal
   */
  const submitHandler = async (onClose) => {
    const publishData = {
      PolicyFramework: markdownContent,
    };
    
    await UpdateOneData("policies", id, publishData);
    
    // Trigger page re-render and close modal
    onReRender(prev => !prev);
    onClose();
  };

  /**
   * Fetch current policy framework content when modal opens
   * Loads existing policy framework text for editing
   */
  useEffect(() => {
    const filterUrl = orgFilterUrl + "&filters[id][$eqi]=" + id;
    
    getDataWithFilterUrl("policies", filterUrl)
      .then((data) => {
        setMarkdownContent(data?.data[0]?.attributes?.PolicyFramework);
      })
      .catch((error) => {
        console.error("Error fetching policy framework:", error);
      });
  }, [id, orgFilterUrl]);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="3xl"
      className=""
    >
      <ModalContent>
        {(onClose) => (
          <>
            {/* Modal Header */}
            <ModalHeader className="flex flex-col gap-1">
              Policy Framework
            </ModalHeader>
            
            {/* Modal Body with Markdown Editor */}
            <ModalBody classNames="">
              <div className="">
                <MarkdownEditor
                  descriptionContent={markdownContent}
                  setDescriptionContent={setMarkdownContent}
                />
              </div>
            </ModalBody>
            
            {/* Modal Footer with Action Buttons */}
            <ModalFooter>
              <StandardCancelButton onPress={onClose} aria-label="Cancel editing policy framework">
                Cancel
              </StandardCancelButton>
              <StandardDeleteButton onClick={() => submitHandler(onClose)} aria-label="Update policy framework">
                Update
              </StandardDeleteButton>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default EditPolicyFrameWorkModal;
